import { findValueColumn } from '@squaredup/data-streams';
import Field from 'components/forms/field/Field';
import { dataMatchCriteria } from 'dashboard-engine/dataStreams/dataMatchCriteria';
import { VisualisationOption } from 'dashboard-engine/types/Visualisation';
import { createOptionGroups, Option, OptionGroups } from '../options';
import { OptionDropdown } from '../sharedComponents/OptionDropdown';
import { valuePrefix } from '../valuePrefix';
import { DataStreamGaugeConfig } from './Config';
import DataStreamGaugeChartOptionsForm, { getValidColumns } from './DataStreamGaugeChartOptionsForm';

export function getDefaultOption(
    config: DataStreamGaugeConfig,
    optionGroups: OptionGroups[],
    builtInOptions: Option[],
    autoName: string
): Option {
    const { VALUES } = valuePrefix;

    // 1) No value at all → auto
    if (!config || !config.value) {
        return { label: autoName, value: VALUES.NONE };
    }

    const configValue = config.value;

    // 2) String value → look for matching column
    if (typeof configValue === 'string') {
        const columnOption = optionGroups[1]?.options.find(
            (opt) => opt.value === valuePrefix.addPrefix.column(configValue)
        );
        if (columnOption) {
            return columnOption;
        }
        return { label: autoName, value: VALUES.NONE };
    }

    // 3) Count type → check built-in count
    if (typeof configValue === 'object' && configValue?.type === 'count') {
        const countOption = builtInOptions.find((opt) => opt.value === VALUES.COUNT);
        if (countOption) {
            return countOption;
        }
    }

    // 4) Fallback → auto
    return { label: autoName, value: VALUES.NONE };
}

export const DataStreamGaugeChartOptions: VisualisationOption<DataStreamGaugeConfig> = {
    initialPanels: (config?: DataStreamGaugeConfig) => {
        const panels = [{ name: 'mapping', isOpen: true }];

        if (config?.minimum || (config?.maximum && config?.maximum !== 100) || config?.label) {
            panels.push({ name: 'options', isOpen: true });
        }

        return panels;
    },
    dataMappingComponent: ({ columns, config, onChange, tileData }) => {
        const columnOptions = getValidColumns(columns);
        const autoColumn = findValueColumn(columnOptions, tileData.rows.length ?? 0);
        const autoName = autoColumn.column ? `Auto - ${autoColumn.displayName}` : 'Auto - Count (rows)';
        const optionGroups = createOptionGroups(columns, autoColumn.column);

        const defaultValue = getDefaultOption(config, optionGroups, optionGroups[0].options, autoName);

        return (
            <Field label='Value' htmlFor='column' help='Select the column to use for the value shown in the tile'>
                <OptionDropdown name='column' options={optionGroups} defaultValue={defaultValue} onChange={onChange} />
            </Field>
        );
    },
    configurationComponent: ({ columns, config, accordionControls, onChange }) => {
        return (
            <DataStreamGaugeChartOptionsForm
                config={config}
                accordionControls={accordionControls}
                onChange={onChange}
                columns={columns}
            />
        );
    },
    handlers: {
        'form-change': (config, data) => {
            return { ...config, ...data };
        },
        'column-select': (config, data) => {
            const { VALUES, hasPrefix, removePrefix } = valuePrefix;

            if (!data) {
                const { value: _, ...rest } = config;
                return rest;
            }

            // Handle built-in prefix
            if (hasPrefix.builtin(data)) {
                if (data === VALUES.COUNT) {
                    return { ...config, value: { type: 'count' } };
                }

                if (data === VALUES.NONE) {
                    const { value: _, ...rest } = config;
                    return rest;
                }
            }

            // Handle column prefix
            if (hasPrefix.column(data)) {
                const columnName = removePrefix(data);
                return { ...config, value: columnName };
            }

            // Backward compatibility
            if (data === 'count') {
                return { ...config, value: { type: 'count' } };
            }

            if (data !== 'none') {
                return { ...config, value: data };
            }

            // Fallback to removing value
            const { value: _, ...rest } = config;
            return rest;
        },
        'label-text': (config, data) => {
            return { ...config, label: data };
        }
    },
    validate: (_, config) => config,
    matchesData: () => dataMatchCriteria(),
    getDefaultConfig: (data): DataStreamGaugeConfig => {
        // Initial default config
        const defaultConfig: DataStreamGaugeConfig = {};

        // Return early if no valid data
        if (!data?.metadata?.columns?.length) {
            return defaultConfig;
        }

        // Find the best column for the gauge
        const columnOptions = getValidColumns(data.metadata.columns);
        const { column } = findValueColumn(columnOptions, data.metadata.rawRowCount);

        // If no suitable column found, return the default count-based config
        if (!column) {
            return defaultConfig;
        }

        // Update config based on the found column
        const isNumeric = column.column.shapeName.includes('number');

        // Set value configuration with the correct type structure
        if (isNumeric) {
            defaultConfig.value = {
                type: 'mean',
                columns: [column.column.name]
            };
        } else {
            defaultConfig.value = {
                type: 'count'
            };
        }

        // For numeric columns, set min/max based on data values
        if (isNumeric) {
            const values = data.rows
                .map((row) => row[column.dataIndex]?.raw)
                .filter((val): val is number => typeof val === 'number');

            if (values.length) {
                const minValue = Math.min(...values);
                const maxValue = Math.max(...values);
                const range = maxValue - minValue;

                defaultConfig.minimum = Math.max(0, minValue - range * 0.1);
                defaultConfig.maximum = maxValue + range * 0.1;
            }
        }

        return defaultConfig;
    }
};
